import { ShoppingCartCheckout } from '@mui/icons-material';
import { Button, Grid, Typography } from '@mui/material';

import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { useOrderMutation, useShoppingCartAvailabilityQuery } from 'api/hooks/leihbar';
import SubmittingLoader from 'app/components/Util/SubmittingLoader';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { isProductABundle } from '../../helper';
import { ShoppingCartItem } from '../ShoppingCartItem';
import { selectShoppingCart } from '../selectors';
import { ShoppingCartActions } from '../slice';

interface MappedItem {
  startTime: Date;
  endTime: Date;
  comment?: string;
  amount: number;
  productId: number;
  optionIds: number[];
}

export const ShoppingCartContent = () => {
  const shoppingCart = useSelector(selectShoppingCart);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [submitting, setSubmitting] = useState(false);

  const { create } = useOrderMutation();

  const { data: shoppingCartAvailability, isLoading } = useShoppingCartAvailabilityQuery();

  useEffect(() => {
    if (!shoppingCart.open || isLoading || !shoppingCartAvailability) return;
    Object.entries(shoppingCartAvailability).forEach(([key, value]) => {
      if (value === false) {
        dispatch(ShoppingCartActions.setAvailabilityOfItem({ id: parseInt(key) }));
      }
    });
  }, [shoppingCart.open, shoppingCart.items, isLoading, shoppingCartAvailability, dispatch]);

  const handleOrder = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const mappedItems: MappedItem[] = [];
      for (const item of shoppingCart.items) {
        if (!isProductABundle(item.product)) {
          mappedItems.push({
            startTime: item.startTime,
            endTime: item.endTime,
            comment: item.comment,
            amount: item.amount,
            //@ts-ignore // TODO remove ts-ignore
            productId: item.product.id,
            optionIds: item.options?.map(option => option.id) || []
          });
        } else {
          if (item.product?.products) {
            for (const product of item.product?.products) {
              mappedItems.push({
                startTime: item.startTime,
                endTime: item.endTime,
                comment: item.comment,
                amount: item.amount,
                productId: product.id,
                optionIds:
                  item.options?.filter(opt => opt.product?.id === product.id).map(opt => opt.id) ||
                  []
              });
            }
          }
        }
      }

      await create.mutateAsync({
        items: mappedItems,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`
      });
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.shoppingCart.alerts.successfulOrder()))
      );
    } catch (error) {
      if (error instanceof AxiosError && error?.response?.status === 412) {
        dispatch(
          AlertActions.error(
            t(translations.pages.leihbar.shoppingCart.alerts.noArticleAvailable(), {
              productId: error?.response.data.productId
            })
          )
        );
      } else {
        dispatch(
          AlertActions.error(t(translations.pages.leihbar.shoppingCart.alerts.failedOrder()))
        );
      }
    }
    dispatch(ShoppingCartActions.close());
    navigate('/leihbar');
    setSubmitting(false);
  };

  return (
    <Grid container padding={'0.5em'} flex={1} flexDirection={'column'} alignContent={'flex-start'}>
      <Typography variant="h4" fontWeight="bold" marginBottom={'0.5em'}>
        {t(translations.pages.leihbar.shoppingCart.title())}
      </Typography>
      <Grid
        container
        maxHeight={'85vh'} // set a max height for the scrolling area
        padding={1}
        sx={{ overflowY: 'auto' }} // enable scrolling
      >
        {!!shoppingCart.items.length ? (
          shoppingCart.items.map(item => <ShoppingCartItem item={item} key={item.id} />)
        ) : (
          <Typography variant="h5" fontWeight="bold">
            {t(translations.pages.leihbar.shoppingCart.empty())}
          </Typography>
        )}
      </Grid>
      <Grid container item justifyContent={'flex-end'} marginTop={'auto'}>
        <span
          style={{
            position: 'relative'
          }}
        >
          <Button
            startIcon={<ShoppingCartCheckout />}
            variant="contained"
            className="button--create"
            onClick={() => {
              handleOrder();
            }}
            disabled={
              shoppingCart.items.length === 0 ||
              submitting ||
              !!shoppingCart.items.filter(item => item.unavailable).length ||
              !!shoppingCart.items.filter(
                item =>
                  !!item.product?.products?.filter(bundleProduct => bundleProduct.unavailable)
                    .length
              )?.length
            }
          >
            {t(translations.pages.leihbar.shoppingCart.orderNow())}
          </Button>
          {submitting && <SubmittingLoader />}
        </span>
      </Grid>
    </Grid>
  );
};
